import {
  Alert,
  Box,
  Button,
  Group,
  NavLink,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconLock } from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import { NavLink as PovezavaRouterja, useNavigate, useParams } from 'react-router';
import { api, podatki } from '../api/odjemalec';
import { NapakaApi, sporocilo } from '../api/napaka';
import { Kartica, StanjeNalaganja } from '../postavitev/Podatki';
import { GlavaZaslona, ZaslonSeznama } from '../seznam/Seznam';
import { barve } from '../tema';
import { FursNastavitve } from './FursNastavitve';
import { Logotip } from './Logotip';
import { Predloge } from './Predloge';
import { useDelavnica } from '../seja/paket';
import { SKUPINE_NASTAVITEV, zaObrazec, type SkupinaNastavitev } from './skupine';
import { Sifranti } from './Sifranti';
import { Uporabniki } from './Uporabniki';
import { Vloge } from './Vloge';
import { sme, usePravice, type Podrocje } from '../seja/pravice';
import { SamoPravica } from '../strani/SamoModul';
import { JavnaPonudba } from './JavnaPonudba';
import { UmetnaInteligenca } from './UmetnaInteligenca';
import { OpisVozil } from './OpisVozil';
import { UrejanjeSlik } from './UrejanjeSlik';
import { PomocIkona } from '../pomoc/PomocIkona';
import { IKONE } from './ikone';

type Skupina = { kljuc: string; naziv: string; opis: string; pomoc?: string; podrocje?: Podrocje };

/**
 * Ostale skupine (lastni zasloni); {@code pomoc} = članek za »?« ob naslovu,
 * {@code podrocje} = pravica, s katero je skupina vidna (privzeto Nastavitve podjetja).
 */
const OSTALE: Skupina[] = [
  {
    kljuc: 'logotip',
    naziv: 'Logotip',
    opis: 'Logotip na dokumentih in njegova velikost.',
    pomoc: 'logotip-glava-noga',
  },
  {
    kljuc: 'predloge',
    naziv: 'Predloge dokumentov',
    opis: 'Besedila pogodb, zapisnikov in garancije.',
    pomoc: 'predloge-dokumentov',
  },
  { kljuc: 'sifranti', naziv: 'Šifranti', opis: 'Vrste vozil in klavzule.' },
  {
    kljuc: 'furs',
    naziv: 'FURS',
    opis: 'Certifikat, poslovni prostori in elektronske naprave.',
    pomoc: 'davcno-potrjevanje',
  },
  {
    kljuc: 'uporabniki',
    naziv: 'Uporabniki',
    opis: 'Uporabniki podjetja, njihove vloge in gesla.',
    pomoc: 'uporabniki',
    podrocje: 'UPORABNIKI',
  },
  {
    kljuc: 'vloge',
    naziv: 'Vloge in pravice',
    opis: 'Kaj smejo uporabniki z določeno vlogo videti in spreminjati.',
    pomoc: 'vloge-in-pravice',
    podrocje: 'UPORABNIKI',
  },
  {
    kljuc: 'javna-ponudba',
    naziv: 'Javna ponudba',
    opis: 'Objava vozil na spletu, povezava do zaloge, kontakt in obvestila o povpraševanjih.',
    pomoc: 'javna-ponudba',
    podrocje: 'PONUDBA',
  },
  {
    kljuc: 'opis-vozil',
    naziv: 'Opis vozil',
    opis: 'Fiksno besedilo pred opisom in za njim pri vseh vozilih ter navodila za umetno inteligenco.',
    pomoc: 'opis-vozila',
  },
  {
    kljuc: 'umetna-inteligenca',
    naziv: 'Umetna inteligenca',
    opis: 'Branje dokumentov in druge funkcije z umetno inteligenco, cena in poraba.',
    pomoc: 'umetna-inteligenca',
    podrocje: 'NAROCNINA',
  },
  {
    kljuc: 'urejanje-slik',
    naziv: 'Urejanje slik',
    opis: 'Urejanje fotografij vozil z umetno inteligenco: tablica z logotipom, ozadje, svetloba in vaša navodila.',
    pomoc: 'urejanje-slik',
  },
];

const kljuciSkupine = (s: SkupinaNastavitev) => s.sklopi.flatMap((k) => k.polja.map((p) => p.kljuc));

/**
 * Nastavitve: levo skupine (na telefonu izbira zgoraj), desno izbrana skupina.
 * Skupine nastavitev se shranijo vsaka posebej; neshranjene spremembe ostanejo ob preklopu skupine in so v meniju
 * označene z rdečo piko. Logotip, predloge, šifranti, FURS in uporabniki imajo svoje zaslone. Vidne so samo skupine,
 * za katere ima uporabnik pravico (vloga); brez urejanja je nad skupino opozorilo (strežnik shranjevanje zavrne).
 */
export function Nastavitve() {
  const { skupina = 'podjetje' } = useParams();
  const navigate = useNavigate();
  const nastavitve = useQuery({
    queryKey: ['nastavitve'],
    queryFn: ({ signal }) => podatki(api.GET('/api/nastavitve', { signal })),
  });
  const [urejeno, setUrejeno] = useState<Record<string, string>>({});
  const delavnica = useDelavnica();
  const pravice = usePravice();
  const shranjeno = nastavitve.data ?? {};
  const spremenjena = (s: SkupinaNastavitev) =>
    kljuciSkupine(s).some((k) => urejeno[k] !== undefined && urejeno[k] !== zaObrazec(k, shranjeno[k]));
  const kateraSpremenjena = SKUPINE_NASTAVITEV.filter(spremenjena).map((s) => s.kljuc);

  useEffect(() => {
    if (kateraSpremenjena.length === 0) {
      return;
    }
    const opozori = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', opozori);
    return () => window.removeEventListener('beforeunload', opozori);
  }, [kateraSpremenjena.length]);

  const vse: Skupina[] = [
    ...SKUPINE_NASTAVITEV.filter((s) => s.modul !== 'delavnica' || delavnica).map((s): Skupina => ({
      kljuc: s.kljuc,
      naziv: s.naziv,
      opis: s.opis,
    })),
    ...OSTALE,
  ].filter((s) => sme(pravice, s.podrocje ?? 'NASTAVITVE'));
  const izbrana = vse.find((s) => s.kljuc === skupina) ?? vse[0];
  if (!izbrana) {
    return (
      <SamoPravica podrocje="NASTAVITVE">
        <></>
      </SamoPravica>
    );
  }
  const samoPregled = !sme(pravice, izbrana.podrocje ?? 'NASTAVITVE', 'UREJANJE');
  const skupinaNastavitev = SKUPINE_NASTAVITEV.find((s) => s.kljuc === izbrana.kljuc);

  let vsebina: ReactNode;
  if (skupinaNastavitev) {
    vsebina = !nastavitve.data ? (
      <StanjeNalaganja
        napaka={nastavitve.error}
        ponovi={() => void nastavitve.refetch()}
        naslov="Nastavitev ni bilo mogoče naložiti"
      />
    ) : (
      <ObrazecSkupine
        key={skupinaNastavitev.kljuc}
        skupina={skupinaNastavitev}
        shranjeno={shranjeno}
        urejeno={urejeno}
        nastavi={(k, v) => setUrejeno((u) => ({ ...u, [k]: v }))}
        shranjena={(kljuci) =>
          setUrejeno((u) => Object.fromEntries(Object.entries(u).filter(([k]) => !kljuci.includes(k))))
        }
      />
    );
  } else if (izbrana.kljuc === 'logotip') {
    vsebina = <Logotip />;
  } else if (izbrana.kljuc === 'predloge') {
    vsebina = <Predloge />;
  } else if (izbrana.kljuc === 'sifranti') {
    vsebina = <Sifranti />;
  } else if (izbrana.kljuc === 'furs') {
    vsebina = <FursNastavitve />;
  } else if (izbrana.kljuc === 'javna-ponudba') {
    vsebina = <JavnaPonudba />;
  } else if (izbrana.kljuc === 'opis-vozil') {
    vsebina = <OpisVozil />;
  } else if (izbrana.kljuc === 'umetna-inteligenca') {
    vsebina = <UmetnaInteligenca />;
  } else if (izbrana.kljuc === 'urejanje-slik') {
    vsebina = <UrejanjeSlik />;
  } else if (izbrana.kljuc === 'vloge') {
    vsebina = <Vloge />;
  } else {
    vsebina = <Uporabniki />;
  }

  return (
    <ZaslonSeznama>
      <GlavaZaslona naslov="Nastavitve" opis="Nastavitve podjetja, dokumentov, šifranti, FURS in uporabniki." />
      <Select
        hiddenFrom="sm"
        aria-label="Skupina nastavitev"
        data={vse.map((s) => ({
          value: s.kljuc,
          label: kateraSpremenjena.includes(s.kljuc) ? `${s.naziv} •` : s.naziv,
        }))}
        value={izbrana.kljuc}
        onChange={(v) => v && void navigate(`/nastavitve/${v}`)}
        allowDeselect={false}
        mb="md"
      />
      <Group align="flex-start" gap="md" wrap="nowrap">
        <Paper radius="lg" p="xs" shadow="xs" w={250} visibleFrom="sm" style={{ flexShrink: 0 }} component="nav">
          {vse.map((s) => (
            <NavLink
              key={s.kljuc}
              component={PovezavaRouterja}
              to={`/nastavitve/${s.kljuc}`}
              label={s.naziv}
              leftSection={IKONE[s.kljuc]}
              active={s.kljuc === izbrana.kljuc}
              rightSection={
                kateraSpremenjena.includes(s.kljuc) ? (
                  <Box
                    w={8}
                    h={8}
                    style={{ borderRadius: 4, background: barve.napaka }}
                    aria-label="Neshranjene spremembe"
                  />
                ) : undefined
              }
              style={{ borderRadius: 10 }}
            />
          ))}
        </Paper>
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Group gap={4} wrap="nowrap">
            <Title order={2} size="h3">
              {izbrana.naziv}
            </Title>
            {izbrana.pomoc && <PomocIkona clanek={izbrana.pomoc} />}
          </Group>
          <Text size="sm" c="dimmed" mb="md">
            {izbrana.opis}
          </Text>
          {samoPregled && (
            <Alert color="yellow" variant="light" mb="md" icon={<IconLock size={18} />}>
              Te nastavitve lahko samo pregledate – vaša vloga jih ne sme spreminjati.
            </Alert>
          )}
          {vsebina}
        </Box>
      </Group>
    </ZaslonSeznama>
  );
}

/** Ena skupina nastavitev (ključ/vrednost) s svojim »Shrani«; napake strežnika ob poljih. */
function ObrazecSkupine({
  skupina,
  shranjeno,
  urejeno,
  nastavi,
  shranjena,
}: {
  skupina: SkupinaNastavitev;
  shranjeno: Record<string, string>;
  urejeno: Record<string, string>;
  nastavi: (kljuc: string, vrednost: string) => void;
  shranjena: (kljuci: string[]) => void;
}) {
  const odjemalec = useQueryClient();
  const [polja, setPolja] = useState<Record<string, string>>({});
  const [napaka, setNapaka] = useState<string | null>(null);
  const [shranjujem, setShranjujem] = useState(false);
  const vrednost = (k: string) => urejeno[k] ?? zaObrazec(k, shranjeno[k]);
  const kljuci = kljuciSkupine(skupina);
  const spremenjeno = kljuci.some((k) => urejeno[k] !== undefined && urejeno[k] !== zaObrazec(k, shranjeno[k]));

  const shrani = async () => {
    setNapaka(null);
    setPolja({});
    setShranjujem(true);
    try {
      const vse = await podatki(
        api.POST('/api/nastavitve/posodobi', { body: Object.fromEntries(kljuci.map((k) => [k, vrednost(k)])) }),
      );
      odjemalec.setQueryData(['nastavitve'], vse);
      shranjena(kljuci);
      notifications.show({ color: 'green', message: `Nastavitve »${skupina.naziv}« so shranjene.` });
    } catch (e) {
      if (e instanceof NapakaApi && Object.keys(e.polja).length > 0) {
        setPolja(e.polja);
        setNapaka('Ni shranjeno: popravite označena polja.');
      } else {
        setNapaka(`Ni shranjeno: ${sporocilo(e)}`);
      }
    } finally {
      setShranjujem(false);
    }
  };

  return (
    <Stack gap="md">
      {skupina.sklopi.map((sklop) => (
        <Kartica
          key={sklop.naslov}
          naslov={sklop.naslov}
          dejanja={sklop.pomoc ? <PomocIkona clanek={sklop.pomoc} /> : undefined}
        >
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="sm">
            {sklop.polja.map((p) => {
              const skupno = {
                label: p.oznaka,
                description: p.opis,
                error: polja[p.kljuc],
                placeholder: p.namig,
              };
              if (p.vrsta === 'da-ne') {
                return (
                  <Switch
                    key={p.kljuc}
                    label={p.oznaka}
                    checked={vrednost(p.kljuc) === 'true'}
                    onChange={(e) => nastavi(p.kljuc, e.currentTarget.checked ? 'true' : 'false')}
                    error={polja[p.kljuc]}
                    mt="sm"
                  />
                );
              }
              if (p.vrsta === 'dolgo') {
                return (
                  <Textarea
                    key={p.kljuc}
                    {...skupno}
                    value={vrednost(p.kljuc)}
                    onChange={(e) => nastavi(p.kljuc, e.currentTarget.value)}
                    autosize
                    minRows={2}
                    maxRows={8}
                    style={{ gridColumn: '1 / -1' }}
                  />
                );
              }
              return (
                <TextInput
                  key={p.kljuc}
                  {...skupno}
                  value={vrednost(p.kljuc)}
                  onChange={(e) => nastavi(p.kljuc, e.currentTarget.value)}
                  inputMode={p.vrsta === 'stevilo' ? 'decimal' : undefined}
                  rightSection={p.kljuc === 'VelikDDVProcenti' || p.kljuc === 'MaliDDVProcenti' ? '%' : undefined}
                  description={
                    p.kljuc === 'predponaStRacuna' ? <PrimerStevilke predpona={vrednost(p.kljuc)} /> : p.opis
                  }
                />
              );
            })}
          </SimpleGrid>
        </Kartica>
      ))}
      {napaka && (
        <Alert color="red" variant="light" icon={<IconAlertCircle size={18} />} role="alert">
          {napaka}
        </Alert>
      )}
      <Group justify="flex-end">
        <Button onClick={() => void shrani()} loading={shranjujem} disabled={!spremenjeno}>
          Shrani
        </Button>
      </Group>
    </Stack>
  );
}

/** Primer naslednje številke računa s predpono (številčenje je na strežniku). */
function PrimerStevilke({ predpona }: { predpona: string }) {
  const [zakasnjena] = useDebouncedValue(predpona, 300);
  const primer = useQuery({
    queryKey: ['nova-stevilka', zakasnjena],
    queryFn: ({ signal }) =>
      podatki(api.GET('/api/racuni/nova-stevilka', { signal, params: { query: { predpona: zakasnjena } } })),
    retry: false,
  });
  if (primer.error) {
    return <>Največ 10 črk, številk in pomišljajev.</>;
  }
  return <>Naslednji račun: {primer.data?.besedilo ?? '…'}</>;
}
